import { sanitizeLibraryReadingMedallionId } from "../constants/libraryReadingMedallions";

const STORAGE_KEY = "vocabularium_library_reading_timer";

export const LIBRARY_READING_TIMER_EVENT = "vocabularium-library-reading-timer";
export const LIBRARY_READING_TIMER_MIN_MINUTES = 5;
export const LIBRARY_READING_TIMER_MAX_MINUTES = 180;
export const LIBRARY_READING_TIMER_DEFAULT_MINUTES = 25;
export const LIBRARY_READING_TIMER_DEFAULT_MINUTE_COLOR = "#41b883";
export const LIBRARY_READING_TIMER_DEFAULT_SECOND_COLOR = "#d9772a";

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function sanitizeLibraryReadingTimerColor(value, fallback) {
  const color = String(value || "").trim();
  return HEX_COLOR_RE.test(color) ? color.toLowerCase() : fallback;
}

export function clampLibraryReadingTimerMinutes(value) {
  const minutes = Math.round(Number(value));
  if (!Number.isFinite(minutes)) return LIBRARY_READING_TIMER_DEFAULT_MINUTES;
  return Math.min(
    LIBRARY_READING_TIMER_MAX_MINUTES,
    Math.max(LIBRARY_READING_TIMER_MIN_MINUTES, minutes)
  );
}

function emptyReadingTimer() {
  return {
    enabled: false,
    minutes: LIBRARY_READING_TIMER_DEFAULT_MINUTES,
    remainingSeconds: LIBRARY_READING_TIMER_DEFAULT_MINUTES * 60,
    paused: false,
    minuteColor: LIBRARY_READING_TIMER_DEFAULT_MINUTE_COLOR,
    secondColor: LIBRARY_READING_TIMER_DEFAULT_SECOND_COLOR,
    discrete: false,
    medallionId: sanitizeLibraryReadingMedallionId(""),
  };
}

function normalizeReadingTimer(parsed) {
  const minutes = clampLibraryReadingTimerMinutes(parsed?.minutes);
  const maxSeconds = minutes * 60;
  const rawRemaining = Number(parsed?.remainingSeconds);
  const remainingSeconds = Number.isFinite(rawRemaining)
    ? Math.min(maxSeconds, Math.max(0, Math.round(rawRemaining)))
    : maxSeconds;
  return {
    enabled: Boolean(parsed?.enabled),
    minutes,
    remainingSeconds,
    paused: Boolean(parsed?.paused),
    minuteColor: sanitizeLibraryReadingTimerColor(
      parsed?.minuteColor,
      LIBRARY_READING_TIMER_DEFAULT_MINUTE_COLOR
    ),
    secondColor: sanitizeLibraryReadingTimerColor(
      parsed?.secondColor,
      LIBRARY_READING_TIMER_DEFAULT_SECOND_COLOR
    ),
    discrete: Boolean(parsed?.discrete),
    medallionId: sanitizeLibraryReadingMedallionId(parsed?.medallionId),
  };
}

function saveReadingTimer(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Le minuteur reste utilisable pour la session même si le stockage est bloqué.
  }
}

export function readLibraryReadingTimer() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return normalizeReadingTimer(parsed);
  } catch {
    return emptyReadingTimer();
  }
}

export function writeLibraryReadingTimer(preference) {
  const previous = readLibraryReadingTimer();
  const minutes = clampLibraryReadingTimerMinutes(preference?.minutes);
  const next = {
    enabled: Boolean(preference?.enabled),
    minutes,
    remainingSeconds:
      minutes === previous.minutes ? previous.remainingSeconds : minutes * 60,
    paused: minutes === previous.minutes ? previous.paused : false,
    minuteColor: sanitizeLibraryReadingTimerColor(
      preference?.minuteColor,
      previous.minuteColor
    ),
    secondColor: sanitizeLibraryReadingTimerColor(
      preference?.secondColor,
      previous.secondColor
    ),
    discrete: preference?.discrete == null ? Boolean(previous.discrete) : Boolean(preference.discrete),
    medallionId: sanitizeLibraryReadingMedallionId(
      preference?.medallionId == null ? previous.medallionId : preference.medallionId
    ),
  };
  saveReadingTimer(next);
  window.dispatchEvent(new CustomEvent(LIBRARY_READING_TIMER_EVENT, { detail: next }));
  return next;
}

const VIEW_KEY = "vocabularium_library_reading_preferences_view";

export function readLibraryReadingPreferencesView() {
  try {
    const parsed = JSON.parse(localStorage.getItem(VIEW_KEY) || "{}");
    const scrollTop = Math.round(Number(parsed?.scrollTop));
    return {
      panel: parsed?.panel === "medallions" ? "medallions" : "settings",
      scrollTop: Number.isFinite(scrollTop) ? Math.max(0, scrollTop) : 0,
    };
  } catch {
    return { panel: "settings", scrollTop: 0 };
  }
}

export function writeLibraryReadingPreferencesView(view) {
  const previous = readLibraryReadingPreferencesView();
  const scrollTop = Math.round(Number(view?.scrollTop));
  const next = {
    panel:
      view?.panel === "medallions" || view?.panel === "settings" ? view.panel : previous.panel,
    scrollTop: Number.isFinite(scrollTop) ? Math.max(0, scrollTop) : previous.scrollTop,
  };
  try {
    localStorage.setItem(VIEW_KEY, JSON.stringify(next));
  } catch {
    // La fenêtre reste utilisable même si le stockage est bloqué.
  }
  return next;
}

export function writeLibraryReadingTimerRemaining(remainingSeconds, paused = undefined) {
  const current = readLibraryReadingTimer();
  const maxSeconds = current.minutes * 60;
  const next = {
    ...current,
    remainingSeconds: Math.min(maxSeconds, Math.max(0, Math.round(Number(remainingSeconds) || 0))),
    paused: paused == null ? current.paused : Boolean(paused),
  };
  saveReadingTimer(next);
  return next;
}
